// Shared "Upstream updates available" banner for the dashboard + releases
// overview. Reads from /api/releases/chromium/upstream and the manifest so
// the card itself owns its data lifecycle — pages just drop it in.
//
// Renders nothing when the catalog hasn't been synced yet so dev / fresh
// installs don't show a confusing empty banner. When everything is in
// sync, a compact "up to date" confirmation is shown instead — handy
// signal that the cron is actually working.

import { useQuery } from "@tanstack/react-query";
import { compareSemver } from "@veil/shared/version";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { formatRelative } from "@veil/ui/lib/format";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";
import { Link } from "wouter";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";

interface ManifestMajor {
	latestBuild: string | null;
}

interface ManifestResponse {
	success: boolean;
	manifest: { browserMajors: Record<string, ManifestMajor> } | null;
}

interface UpstreamRelease {
	fullVersion: string;
	browserMajor: number;
	publishedAt: string;
	hasX64Deb: boolean;
}

interface UpstreamResponse {
	success: boolean;
	releases: UpstreamRelease[];
	syncedAt: string | null;
}

export function UpstreamDriftCard({
	variant = "default",
}: {
	variant?: "default" | "compact";
}) {
	const { user } = useAuth();
	const canPublish = hasRole(user, "operator");

	const manifestQuery = useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
		refetchInterval: 60_000,
	});

	const upstreamQuery = useQuery({
		queryKey: ["chromium-upstream"],
		queryFn: async () => {
			const res = await fetch("/api/releases/chromium/upstream", {
				credentials: "same-origin",
			});
			if (!res.ok) {
				if (res.status === 404 || res.status === 501) {
					return {
						success: true,
						releases: [],
						syncedAt: null,
					} satisfies UpstreamResponse;
				}
				throw new Error(`upstream ${res.status}`);
			}
			return (await res.json()) as UpstreamResponse;
		},
		refetchInterval: 5 * 60_000,
		retry: false,
	});

	const drift = useMemo(() => {
		const browserMajors = manifestQuery.data?.manifest?.browserMajors ?? {};
		const upstreamByMajor = new Map<number, string>();
		for (const r of upstreamQuery.data?.releases ?? []) {
			if (!r.hasX64Deb) continue;
			const prev = upstreamByMajor.get(r.browserMajor);
			if (!prev || compareSemver(r.fullVersion, prev) > 0) {
				upstreamByMajor.set(r.browserMajor, r.fullVersion);
			}
		}
		const items: Array<{
			major: number;
			ours: string | null;
			upstream: string;
		}> = [];
		for (const [m, upstream] of upstreamByMajor.entries()) {
			const ours = browserMajors[String(m)]?.latestBuild ?? null;
			if (!ours || compareSemver(upstream, ours) > 0) {
				items.push({ major: m, ours, upstream });
			}
		}
		items.sort((a, b) => b.major - a.major);
		return {
			items,
			syncedAt: upstreamQuery.data?.syncedAt ?? null,
		};
	}, [upstreamQuery.data, manifestQuery.data]);

	const loading = upstreamQuery.isPending || manifestQuery.isPending;

	if (loading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<AlertTriangle className="size-4 text-amber-500" />
						Upstream updates available
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Skeleton className="h-10 w-full" />
				</CardContent>
			</Card>
		);
	}

	if (drift.syncedAt === null) {
		return null;
	}

	if (drift.items.length === 0) {
		if (variant === "compact") return null;
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<CheckCircle2 className="size-4 text-emerald-500" />
						Up to date with upstream
					</CardTitle>
					<CardDescription>
						Upstream catalog last synced {formatRelative(drift.syncedAt)} —
						every published major matches the latest upstream version.
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2 text-base">
					<AlertTriangle className="size-4 text-amber-500" />
					Upstream updates available
				</CardTitle>
				<CardDescription>
					Upstream catalog last synced {formatRelative(drift.syncedAt)}.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<ul className="flex flex-col gap-2">
					{drift.items.map((it) => (
						<li
							key={it.major}
							className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm"
						>
							<Badge variant="outline" className="font-mono">
								v{it.major}
							</Badge>
							<span className="font-mono text-xs text-muted-foreground">
								{it.ours ?? "—"} → {it.upstream}
							</span>
							<div className="ml-auto">
								{canPublish ? (
									<Button asChild size="sm" variant="outline">
										<Link
											href={`/releases/new/chromium?version=${encodeURIComponent(it.upstream)}`}
										>
											Publish {it.upstream}
										</Link>
									</Button>
								) : (
									<Button asChild size="sm" variant="ghost">
										<Link href={`/releases/chromium/${it.major}`}>
											View v{it.major}
										</Link>
									</Button>
								)}
							</div>
						</li>
					))}
				</ul>
			</CardContent>
		</Card>
	);
}
